<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { get } from 'svelte/store';
  import type { AppContext, TimelineEvent } from '../app';
  import { cue, haptic } from '../audio/sound';
  import { momentWindow } from '../core/moments';
  import { createTelemetry } from '../core/telemetry';
  import { lang, t } from '../i18n';
  import { currentDate, focus, followTarget, selectedMission, titleCardHeight } from '../state/stores';
  import { crossed, formatCountdown } from './countdown';
  import { formatKm } from './format';

  // Flyby title card (S2-10, style B "placard"): appears while the active spacecraft is near a flyby (2 days before to
  // 12 hours after closest approach, mission time) and stays at least HOLD_MS on screen, so fast playback cannot
  // flash it away. Replaces the toast for flybys; the live distance comes from the scene's telemetry.
  let { ctx }: { ctx: AppContext } = $props();

  const HOLD_MS = 4000;
  /**
   * When the clock jumped over the moment between two samples (fast playback), "Closest approach" / "Touchdown" stays
   * this long in real time (S4-15 item 2: 8 of 12 flybys showed no such state at 1 month/s).
   */
  const NOW_HOLD_MS = 1500;

  // moments (S4-13): flybys, landings (Huygens on Titan, S3-18: "TITAN · Landing", with the altitude instead of the
  // range) and, for crewed missions, orbit insertions and burns ("MOON · Into orbit around the Moon")
  const flybys = $derived(ctx.events.filter((e) => e.moment && e.spacecraft));
  // title cards only for the followed (or focused) spacecraft (D13); the fleet's other flybys become toasts
  // title cards for the followed spacecraft, else the focused one, else the selected mission's (D13)
  const scIds = $derived(
    $followTarget
      ? [$followTarget]
      : ctx.targets.get($focus)?.kind === 'spacecraft'
        ? [$focus]
        : [...ctx.targets.values()].filter((x) => x.mission && x.mission === $selectedMission).map((x) => x.id),
  );

  let card = $state<TimelineEvent | null>(null);
  let closestKm = $state(Number.NaN);
  let rangeKm = $state(Number.NaN);
  let countdown = $state<string | null>(null);
  let announcement = $state('');
  let cardHeight = $state(0);
  // the scene keeps the planet and spacecraft below the card (8 px gap)
  $effect(() => {
    titleCardHeight.set(card ? cardHeight + 8 : 0);
  });
  onDestroy(() => titleCardHeight.set(0));

  const reading = createTelemetry();
  let lastInWindow = 0;
  let pinged = false;
  let raf = 0;
  let lastTick = 0;
  /** Mission time at the previous sample, and until when (real time) the moment holds after a crossing. */
  let lastNow = Number.NaN;
  let nowUntil = 0;

  function tick(wall: number): void {
    raf = requestAnimationFrame(tick);
    if (wall - lastTick < 100) return;
    lastTick = wall;
    const now = get(currentDate);
    const before = lastNow;
    lastNow = now;
    const event = flybys.find((e) => {
      const w = momentWindow(!!e.crewed);
      return scIds.includes(e.spacecraft!) && now >= e.time - w.before && now <= e.time + w.after;
    });
    if (event) {
      lastInWindow = wall;
      if (event !== card) {
        card = event;
        pinged = false;
        nowUntil = 0;
        cue('swell');
        closestKm = ctx.scene.distanceAt(event.spacecraft!, event.body!, event.time) ?? Number.NaN;
        announcement = event.title[get(lang)];
      }
    } else if (card && wall - lastInWindow > HOLD_MS) {
      card = null;
      announcement = '';
    }
    if (!card) return;
    if (crossed(before, now, card.time)) nowUntil = wall + NOW_HOLD_MS;
    countdown = wall < nowUntil ? null : formatCountdown(now - card.time);
    if (countdown === null && !pinged) {
      pinged = true;
      cue('ping');
      haptic();
    }
    const near = ctx.scene.telemetry(card.spacecraft!, reading);
    rangeKm = near === card.body ? reading.nearDistanceKm : Number.NaN;
    // everything but a flyby is told by its altitude above the surface
    if (card.type !== 'flyby') rangeKm = Math.max(0, rangeKm - ctx.scene.bodyRadiusKm(card.body!));
  }
  onMount(() => (raf = requestAnimationFrame(tick)));
  onDestroy(() => cancelAnimationFrame(raf));

  const bodyName = $derived(card?.body ? (ctx.targets.get(card.body)?.name[$lang] ?? card.body) : '');
  // a landing names its site (S4-11: "MOON · Landing · Tranquility Base")
  const site = $derived(
    card?.type === 'landing'
      ? ctx.missions.flatMap((m) => m.data.spacecraft).find((sc) => sc.id === card!.spacecraft)?.landing?.site?.[$lang]
      : undefined,
  );
  const dateText = $derived(
    card
      ? new Intl.DateTimeFormat($lang === 'de' ? 'de-DE' : 'en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(card.time)
      : '',
  );
  const range = $derived(formatKm(rangeKm, $lang));
  const closest = $derived(formatKm(closestKm, $lang));
</script>

<p class="visually-hidden" aria-live="polite">{announcement}</p>
{#if card}
  <section
    class="panel card"
    aria-label={$t('titleCard.label')}
    data-testid="title-card"
    bind:offsetHeight={cardHeight}
  >
    <div class="band">
      <span class="state"
        >{card.type === 'landing'
          ? countdown
            ? $t('titleCard.landing')
            : $t('titleCard.landed')
          : card.type === 'flyby'
            ? countdown
              ? $t('titleCard.encounter')
              : $t('titleCard.closest')
            : card.title[$lang]}</span
      >
      {#if countdown}<span class="mono">{countdown}</span>{/if}
    </div>
    <div class="body">
      <div>
        <h2>{bodyName}</h2>
        <div class="date" data-testid="title-card-date">{site ? `${site} · ${dateText}` : dateText}</div>
      </div>
      <div class="range">
        {#if Number.isFinite(rangeKm)}
          <div class="caps-label">{card.type === 'flyby' ? $t('titleCard.range') : $t('titleCard.altitude')}</div>
          <div class="mono value">{range.value} {range.millions ? $t('unit.millionKm') : 'km'}</div>
        {/if}
        {#if Number.isFinite(closestKm) && card.type === 'flyby'}
          <div class="closest">{$t('titleCard.closestRange', { km: closest.value })}</div>
        {/if}
      </div>
    </div>
  </section>
{/if}

<style>
  .card {
    position: fixed;
    top: calc(var(--hud-top) + 8px);
    left: 50%;
    transform: translateX(-50%);
    width: min(calc(100vw - 32px), 440px);
    padding: 0;
    pointer-events: none;
    animation: in var(--dur) var(--ease);
  }
  .band {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 14px;
    background: var(--signal);
    color: var(--on-signal);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: var(--track-label);
    text-transform: uppercase;
  }
  .state {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .band .mono {
    flex: none;
    margin-left: 8px;
  }
  .mono {
    font-family: var(--font-mono);
    letter-spacing: 0.02em;
  }
  .body {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding: 10px 14px 12px;
  }
  h2 {
    margin: 0;
    font-size: 1.9rem;
    line-height: 1.1;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
  }
  .date {
    margin-top: 2px;
    color: var(--fg-muted);
    font-size: 0.85rem;
  }
  .range {
    text-align: right;
  }
  .value {
    color: var(--accent);
    font-size: 1.05rem;
    font-weight: 600;
    white-space: nowrap;
  }
  .closest {
    margin-top: 2px;
    color: var(--fg-muted);
    font-family: var(--font-mono);
    font-size: 0.7rem;
    white-space: nowrap;
  }
  @keyframes in {
    from {
      opacity: 0;
      transform: translate(-50%, -10px);
    }
  }
  /* phones (S4-15): one line — the band (state, countdown) beside the body and the range; the date and the closest
     range are left to the panel */
  @media (max-width: 899px) {
    .card {
      display: flex;
      align-items: stretch;
    }
    .band {
      flex: 0 1 auto;
      min-width: 0;
      max-width: 55%;
      gap: 8px;
      padding: 2px 10px;
      font-size: 0.62rem;
    }
    .body {
      flex: 1;
      min-width: 0;
      align-items: center;
      padding: 2px 10px;
    }
    h2 {
      font-size: 1.05rem;
      letter-spacing: 0.16em;
    }
    .date,
    .closest,
    .range .caps-label {
      display: none;
    }
    .value {
      font-size: 0.95rem;
    }
  }
</style>
